import {
    useCallback,
    useEffect,
    useMemo,
    useState,
    type ChangeEvent,
    type FC,
    type FocusEvent,
    type KeyboardEvent,
    type MouseEvent,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'dibs-react-intl';
import { SbSharedRefineMenuInputRangeApplyButton } from '../SbSharedRefineMenuInputRange/SbSharedRefineMenuInputRangeApplyButton.tsx';

import { updateUriRef } from '../../../actions/filterActions.ts';
import { type TrackEventEventArgument } from '../../../utils/tracking/searchBrowse/filterTracking.ts';
import {
    formatRange,
    parseRangeDisplayValue,
    constructCaratWeightStatsRange,
    roundCaratWeightRange,
} from '../sbSharedRefineMenuHelpers.ts';
import { useAppliedFilterUrlLabel } from '../../helpers/appliedFilters/useAppliedFilterUrlLabel.ts';
import { useClientState } from 'dibs-react-hooks/src/useClientState.ts';
import { useSbSelector } from '../../../reducers/useSbSelector.ts';
import { getIsRangeFiltersUiVariant } from '../../../utils/abTestHelper.ts';

import { Input } from 'dibs-elements/exports/Input.tsx';
import { Button } from 'dibs-elements/exports/Button.tsx';
import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight.ts';

import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from '../SbSharedRefineMenuInputRange/SbSharedRefineMenuInputRange.module.css';

import { type FilterValue } from '../SbSharedRefineMenuMultiSelect/SbSharedRefineMenuMultiSelect.tsx';
import { type SbSharedRefineMenuCaratWeightRange_itemSearch$key } from './__generated__/SbSharedRefineMenuCaratWeightRange_itemSearch.graphql.ts';

const itemSearchFragment = graphql`
    fragment SbSharedRefineMenuCaratWeightRange_itemSearch on ItemSearchQueryConnection {
        ...useAppliedFilterUrlLabel_itemSearch
    }
`;

const hasInvalidRange = (minValue: string, maxValue: string): boolean => {
    return parseFloat(minValue) > parseFloat(maxValue);
};

type Props = {
    itemSearch: SbSharedRefineMenuCaratWeightRange_itemSearch$key;
    filterName: string;
    filterValue: FilterValue | null | undefined;
    applyOnBlur?: boolean;
};

export const SbSharedRefineMenuCaratWeightRange: FC<Props> = ({
    itemSearch: itemSearchRef,
    filterName,
    filterValue,
    applyOnBlur = false,
}) => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const dispatch = useDispatch();
    const isClient = useClientState();
    const refetchInFlight = useSbSelector(state => state.filters.refetchInFlight);
    const isRangeFiltersUiVariant = isClient && getIsRangeFiltersUiVariant();
    const appliedDisplayValue = useAppliedFilterUrlLabel(itemSearch, filterName);
    const { displayName, linkReference } = filterValue || {};

    const { currentMin, currentMax, availableMin, availableMax } = useMemo(() => {
        const [computedMin, computedMax] = constructCaratWeightStatsRange(
            appliedDisplayValue,
            displayName
        );
        const [parsedMin, parsedMax] = parseRangeDisplayValue(displayName);
        const [computedAvailableMin, computedAvailableMax] = roundCaratWeightRange(
            parsedMin,
            parsedMax
        );

        return {
            currentMin: computedMin.toString(),
            currentMax: computedMax.toString(),
            availableMin: computedAvailableMin,
            availableMax: computedAvailableMax,
        };
    }, [appliedDisplayValue, displayName]);

    const [hasError, setHasError] = useState(false);
    const [minValue, setMinValue] = useState(currentMin);
    const [maxValue, setMaxValue] = useState(currentMax);

    const onApply = useCallback(
        (event?: TrackEventEventArgument, ga?: Record<string, unknown>) => {
            if (minValue !== currentMin || maxValue !== currentMax) {
                const uriRef = formatRange(linkReference || '', [minValue, maxValue]);
                dispatch(updateUriRef({ filterName, uriRef, event, ga }));
            }
        },
        [currentMax, currentMin, dispatch, filterName, linkReference, maxValue, minValue]
    );

    useEffect(() => {
        if (!refetchInFlight) {
            setMinValue(currentMin);
            setMaxValue(currentMax);
        }
    }, [currentMin, currentMax, refetchInFlight]);

    const onChange = useCallback(
        (name: 'min' | 'max', event: ChangeEvent<HTMLInputElement>) => {
            event.preventDefault();
            const rawValue = event.target.value;
            let nextMinValue = minValue;
            let nextMaxValue = maxValue;

            if (name === 'min') {
                const value =
                    parseFloat(rawValue) < availableMin ? availableMin.toString() : rawValue;
                nextMinValue = value;
                setMinValue(value);
            } else {
                const value =
                    parseFloat(rawValue) > availableMax ? availableMax.toString() : rawValue;
                nextMaxValue = value;
                setMaxValue(value);
            }
            setHasError(hasInvalidRange(nextMinValue, nextMaxValue));
        },
        [availableMax, availableMin, maxValue, minValue]
    );

    const onBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            if (!hasError && applyOnBlur) {
                onApply(event, { isInteractiveEvent: true });
            }
        },
        [applyOnBlur, hasError, onApply]
    );

    const applyValue = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            event.preventDefault();

            if (!hasError) {
                onApply(event);
            }
        },
        [hasError, onApply]
    );

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key !== 'Enter' || !isRangeFiltersUiVariant || hasError || refetchInFlight) {
                return;
            }
            event.preventDefault();

            onApply(event, applyOnBlur ? { isInteractiveEvent: true } : undefined);
        },
        [applyOnBlur, isRangeFiltersUiVariant, hasError, onApply, refetchInFlight]
    );

    return (
        <div>
            <div className={classnames(dibsCss.flex, dibsCss.pt1px, dibsCss.pbSmall)}>
                <Input
                    name="carat-weight-min"
                    onChange={event => onChange('min', event)}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    value={minValue.toString()}
                    dataTn="search-browse-carat-weight-input-min"
                    hasError={hasError}
                    horizontalSpacing="small"
                    size="large"
                    showIcons={false}
                    maxLength={17}
                    disabled={refetchInFlight}
                />
                <div
                    className={classnames(
                        dibsCss.selfCenter,
                        dibsCss.mxXsmall,
                        dibsCss.sassyFontBodySizeSmallTypeNone
                    )}
                >
                    <FormattedMessage
                        id="sb.SbSharedRefineMenuCaratWeight.inputRange.to"
                        defaultMessage="to"
                    />
                </div>
                <Input
                    name="carat-weight-max"
                    onChange={event => onChange('max', event)}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    value={maxValue.toString()}
                    dataTn="search-browse-carat-weight-input-max"
                    hasError={hasError}
                    horizontalSpacing="small"
                    size="large"
                    showIcons={false}
                    maxLength={17}
                    disabled={refetchInFlight}
                />
                {!applyOnBlur &&
                    isClient &&
                    (isRangeFiltersUiVariant ? (
                        <SbSharedRefineMenuInputRangeApplyButton
                            disabled={refetchInFlight}
                            hasError={hasError}
                            onClick={applyValue}
                        />
                    ) : (
                        <Button
                            disabled={refetchInFlight}
                            size="large"
                            type="secondary"
                            onClick={applyValue}
                            className={classnames(styles.inputRangeButton, dibsCss.flexShrink0)}
                            dataTn="search-browse-carat-weight-apply"
                        >
                            <ArrowRight className={classnames(dibsCss.w16px, dibsCss.hFull)} />
                        </Button>
                    ))}
            </div>
            {hasError && (
                <div
                    className={classnames(
                        dibsCss.sassyFontBodySizeXSmallTypeNone,
                        dibsCss.textAlertprimary,
                        dibsCss.mbSmaller
                    )}
                >
                    <FormattedMessage
                        id="sb.SbSharedRefineMenuCaratWeight.inputRange.error"
                        defaultMessage="Min value may not exceed max"
                    />
                </div>
            )}
        </div>
    );
};
